import { useState, useEffect } from 'react';
import { authService, AuthState } from '../services/auth-service';

/**
 * React hook for accessing authentication state and methods
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const { isAuthenticated, user, loginWithEmail, logout } = useAuth();
 *
 *   const handleLogin = async () => {
 *     await loginWithEmail('user@example.com', 'password123');
 *   };
 *
 *   return (
 *     <div>
 *       {isAuthenticated ? (
 *         <p>Welcome, {user?.email}</p>
 *       ) : (
 *         <button onClick={handleLogin}>Login</button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth() {
  const [state, setState] = useState<AuthState>(authService.getState());

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = authService.subscribe(setState);

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  return {
    // State
    isAuthenticated: state.isAuthenticated,
    user: state.user,
    token: state.token,

    // Methods
    signupWithEmail: async (email: string, username: string, password: string) => {
      return authService.signupWithEmail(email, username, password);
    },

    loginWithEmail: async (email: string, password: string) => {
      return authService.loginWithEmail(email, password);
    },

    registerPasskey: async (email: string, displayName: string) => {
      return authService.registerPasskey(email, displayName);
    },

    loginWithPasskey: async (email: string) => {
      return authService.loginWithPasskey(email);
    },

    logout: () => {
      authService.logout();
    },

    getToken: () => {
      return authService.getToken();
    },
  };
}
